import { supabase } from '../../lib/supabase';
// Service pour communiquer avec FastAPI en utilisant l'authentification Supabase
class MessageService {
    constructor() {
        this.baseURL = process.env.REACT_APP_API_URL || 'https://ai-agent-api-eh7a.onrender.com';
        this.currentConversationId = localStorage.getItem('finai_conversation_id');
        this.isProcessing = false;

        // Cache pour éviter les appels répétés
        this.cachedToken = null;
        this.tokenExpiry = null;
    }

    // Récupérer le token Supabase avec cache optimisé
    async getSupabaseToken() {
        // Cache plus agressif - vérifier d'abord si le token est encore valide (marge de 10 minutes)
        if (this.cachedToken && this.tokenExpiry && this.tokenExpiry > Date.now() + (10 * 60 * 1000)) {
            console.log('🔒 Token utilisé depuis le cache (expire dans', Math.round((this.tokenExpiry - Date.now()) / 60000), 'min)');
            return this.cachedToken;
        }

        console.log('🔒 Récupération nouveau token Supabase');
        try {
            const { data: { session } } = await supabase.auth.getSession();

            if (session?.access_token) {
                this.cachedToken = session.access_token;

                // Parser le JWT pour récupérer l'expiration réelle
                const payload = JSON.parse(atob(session.access_token.split('.')[1]));
                this.tokenExpiry = payload.exp * 1000; // Convertir en millisecondes

                console.log('🔒 Nouveau token mis en cache (expire dans', Math.round((this.tokenExpiry - Date.now()) / 60000), 'min)');
                return this.cachedToken;
            }

            // Pas de session valide
            console.log('❌ Pas de session Supabase valide');
            this.clearTokenCache();
            return null;

        } catch (error) {
            console.error('Erreur récupération token:', error);
            this.clearTokenCache();
            return null;
        }
    }

    // Nettoyer le cache du token
    clearTokenCache() {
        this.cachedToken = null;
        this.tokenExpiry = null;
    }

    // Configuration des headers avec token Supabase et logs
    async getHeaders() {
        const token = await this.getSupabaseToken();

        if (!token) {
            throw new Error('Non authentifié. Veuillez vous reconnecter.');
        }

        return {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        };
    }

    // Faire une requête à FastAPI avec authentification Supabase
    async request(endpoint, options = {}) {
        const url = `${this.baseURL}${endpoint}`;

        const config = {
            headers: await this.getHeaders(),
            ...options,
        };

        console.log('🌐 Requête FastAPI:', endpoint);

        try {
            const response = await fetch(url, config);

            if (!response.ok) {
                if (response.status === 401) {
                    // Token expiré ou invalide - nettoyer le cache
                    this.clearTokenCache();
                    throw new Error('Session expirée. Veuillez vous reconnecter.');
                }
                if (response.status === 429) {
                    throw new Error('Trop de requêtes. Veuillez patienter un moment.');
                }
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || `Erreur HTTP: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('FastAPI request failed:', error);
            throw error;
        }
    }

    // Nettoyer seulement les données locales (pas d'auth FastAPI)
    clearLocalSession() {
        this.currentConversationId = null;
        localStorage.removeItem('finai_conversation_id');
    }

    // Déconnexion (sera gérée par AuthContext/Supabase)
    logout() {
        this.clearTokenCache(); // Nettoyer le cache
        this.clearLocalSession();
    }

    async getProfile() {
        try {
            const response = await this.request('/auth/me');
            return response;
        } catch (error) {
            throw new Error(`Erreur de profil: ${error.message}`);
        }
    }

    // Traitement des messages - INCHANGÉ
    async processMessage(messageContent) {
        if (this.isProcessing) {
            throw new Error('Un message est déjà en cours de traitement');
        }

        this.isProcessing = true;

        try {
            const payload = {
                message: messageContent
            };

            // Ajouter l'ID de conversation s'il existe
            if (this.currentConversationId) {
                payload.conversation_id = this.currentConversationId;
            }

            const response = await this.request('/chat/', {
                method: 'POST',
                body: JSON.stringify(payload),
            });

            // Sauvegarder l'ID de conversation pour les prochains messages
            if (response.conversation_id) {
                this.currentConversationId = response.conversation_id;
                localStorage.setItem('finai_conversation_id', this.currentConversationId);
            }

            return response.message;
        } catch (error) {
            throw new Error(`Erreur lors du traitement du message: ${error.message}`);
        } finally {
            this.isProcessing = false;
        }
    }

    // Nouvelle conversation - INCHANGÉ
    startNewConversation() {
        this.currentConversationId = null;
        localStorage.removeItem('finai_conversation_id');
    }

    // Récupérer toutes les conversations - INCHANGÉ
    async getConversations() {
        try {
            return await this.request('/chat/conversations');
        } catch (error) {
            console.error('Erreur lors de la récupération des conversations:', error);
            return []; // Retourner liste vide plutôt que planter
        }
    }

    // Récupérer une conversation spécifique - INCHANGÉ
    async getConversation(conversationId) {
        try {
            return await this.request(`/chat/conversations/${conversationId}`);
        } catch (error) {
            throw new Error(`Erreur lors de la récupération de la conversation: ${error.message}`);
        }
    }

    // Charger une conversation existante - INCHANGÉ
    loadConversation(conversationId) {
        this.currentConversationId = conversationId;
        localStorage.setItem('finai_conversation_id', conversationId);
    }

    // Vérifier si l'utilisateur est connecté - MODIFIÉ
    async isAuthenticated() {
        try {
            const token = await this.getSupabaseToken();
            return !!token;
        } catch {
            return false;
        }
    }

    // Vérifier si le service est occupé - INCHANGÉ
    isBusy() {
        return this.isProcessing;
    }

    // Obtenir l'ID de conversation actuelle - INCHANGÉ
    getCurrentConversationId() {
        return this.currentConversationId;
    }
}

// Export d'une instance singleton
const messageService = new MessageService();
export default messageService;